import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { PLACES, CARDS, mapTextures, scrollTexture, globeTexture, woodTexture, cardTextures, boxTexture, letterTexture } from './parchment';

/**
 * The Cartographer's Table: one candle-lit war table, filmed by a camera on a rail.
 * The page's scroll is the timeline. Chapters:
 *   0 the table            (the map's edges burn in)
 *   1 the map              (ink draws itself, buildings rise as wireframes then fill solid)
 *   2 Project 01           (the wax seal bursts, the scroll unrolls to a countdown)
 *   3 the realm            (a brass globe of the world)
 *   4 join                 (a cold candle that you light by signing up)
 */

/** 15 stops on one table. Index: 0 table, 1 junk, 2 map, 3 road, 4 Cinder, 5-9 cards, 10 Project 01,
 *  11 realm, 12 founder, 13 join, 14 finale. */
export const CHAPTERS = Array.from({ length: 15 }, (_, i) => i / 14);
const C = (i: number) => CHAPTERS[i];
const STEP = 1 / 14;
const CARD_X = [-1.6, -0.9, -0.2, 0.5, 1.2];
const CARD_Z = 1.66;
const MAP = { cx: -0.55, cz: 0.05, w: 3.3, d: 2.23 };
const mapAt = (u: number, v: number, y = 0.004) => new THREE.Vector3(MAP.cx + (u - 0.5) * MAP.w, y, MAP.cz + (v - 0.5) * MAP.d);
const ease = (x: number) => x * x * (3 - 2 * x);
const band = (p: number, a: number, b: number) => THREE.MathUtils.clamp((p - a) / (b - a), 0, 1);

// ---------- shaders
const PARCHMENT_HEAD = /* glsl */ `
  uniform sampler2D uInk; uniform float uEdge, uInkK, uTime;
  float ph(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float pn(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
    return mix(mix(ph(i), ph(i+vec2(1,0)), f.x), mix(ph(i+vec2(0,1)), ph(i+vec2(1,1)), f.x), f.y); }
  float pfbm(vec2 p){ return pn(p)*0.55 + pn(p*2.3)*0.28 + pn(p*5.3)*0.17; }
  float pGlow = 0.0;`;
const PARCHMENT_MAP = /* glsl */ `
  {
    // Burnt, irregular edge that recedes as the map "burns in" (uEdge 0 -> 1).
    vec2 c = abs(vMapUv - 0.5) * 2.0;
    float ragged = 1.0 - max(c.x, c.y) + (pfbm(vMapUv * 9.0) - 0.5) * 0.09;
    float cut = mix(1.2, 0.012, uEdge);
    if (ragged < cut) discard;
    float charK = smoothstep(cut + 0.07, cut, ragged);
    pGlow = smoothstep(cut + 0.02, cut, ragged) * (1.0 - uEdge * 0.8);
    // Ink draws itself outward from the bazaar, along a noisy front.
    float front = distance(vMapUv, vec2(0.45, 0.5)) * 1.25 + pfbm(vMapUv * 6.0) * 0.25;
    float inked = smoothstep(uInkK * 1.4 - 0.05, uInkK * 1.4 - 0.12, front);
    vec3 col = mix(texture2D(map, vMapUv).rgb, texture2D(uInk, vMapUv).rgb, inked);
    col = mix(col, vec3(0.12, 0.07, 0.03), charK * 0.85);
    diffuseColor.rgb *= col;
  }`;

const FLAME_VS = /* glsl */ `
  varying vec2 vUv;
  void main(){
    vUv = uv;
    // Billboard: always face the camera, keep world scale.
    vec4 mv = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
    vec2 s = vec2(length(modelMatrix[0].xyz), length(modelMatrix[1].xyz));
    mv.xy += position.xy * s;
    gl_Position = projectionMatrix * mv;
  }`;
const FLAME_FS = /* glsl */ `
  uniform float uTime, uSeed, uLit;
  varying vec2 vUv;
  float h(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float n(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
    return mix(mix(h(i), h(i+vec2(1,0)), f.x), mix(h(i+vec2(0,1)), h(i+vec2(1,1)), f.x), f.y); }
  void main(){
    vec2 p = vUv - vec2(0.5, 0.18);
    float t = uTime * 3.0 + uSeed * 10.0;
    p.x += (n(vec2(p.y * 4.0 - t, uSeed)) - 0.5) * 0.18 * p.y * 2.5;
    // Teardrop: wide at the base, tapering up.
    float w = mix(0.2, 0.02, clamp(p.y / 0.75, 0.0, 1.0));
    float d = abs(p.x) / max(w, 1e-3);
    float body = (1.0 - smoothstep(0.55, 1.0, d)) * smoothstep(-0.12, 0.0, p.y) * (1.0 - smoothstep(0.55, 0.78, p.y));
    float core = (1.0 - smoothstep(0.0, 0.5, d)) * smoothstep(-0.05, 0.05, p.y) * (1.0 - smoothstep(0.2, 0.42, p.y));
    vec3 col = mix(vec3(1.0, 0.42, 0.08), vec3(1.0, 0.93, 0.7), core);
    col += vec3(0.2, 0.35, 1.0) * smoothstep(0.08, -0.06, p.y) * body * 0.6; // blue root
    float a = body * uLit;
    gl_FragColor = vec4(col * (1.6 + core * 2.2), a);
  }`;

const BEAM_VS = /* glsl */ `
  varying vec3 vPos; varying vec3 vN; varying vec3 vV; varying vec2 vUv;
  void main(){ vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vPos = w.xyz;
    vN = normalize(mat3(modelMatrix) * normal); vV = normalize(cameraPosition - w.xyz);
    gl_Position = projectionMatrix * viewMatrix * w; }`;
const BEAM_FS = /* glsl */ `
  uniform float uTime; varying vec3 vPos; varying vec3 vN; varying vec3 vV; varying vec2 vUv;
  float h(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float n(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
    return mix(mix(h(i), h(i+vec2(1,0)), f.x), mix(h(i+vec2(0,1)), h(i+vec2(1,1)), f.x), f.y); }
  void main(){
    float soft = pow(abs(dot(normalize(vN), vV)), 1.6);       // fade at the cone's silhouette
    float along = smoothstep(0.0, 0.25, vUv.y) * (1.0 - smoothstep(0.75, 1.0, vUv.y));
    float streaks = 0.55 + 0.45 * n(vec2(vUv.x * 18.0, vUv.y * 2.0 - uTime * 0.05));
    float a = soft * along * streaks * 0.075;
    gl_FragColor = vec4(vec3(0.62, 0.72, 1.0) * a, a);
  }`;

const TILT = {
  uniforms: { tDiffuse: { value: null }, uK: { value: 0 }, uRes: { value: new THREE.Vector2(1, 1) } },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse; uniform float uK; uniform vec2 uRes; varying vec2 vUv;
    void main(){
      float b = smoothstep(0.1, 0.5, abs(vUv.y - 0.52)) * uK * 3.2;
      vec4 c = vec4(0.0);
      for (int i = -3; i <= 3; i++) for (int j = -3; j <= 3; j++) c += texture2D(tDiffuse, vUv + vec2(float(i), float(j)) * b / uRes);
      gl_FragColor = c / 49.0;
    }`,
};

const FINAL = {
  uniforms: { tDiffuse: { value: null }, uTime: { value: 0 }, uAspect: { value: 1 } },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse; uniform float uTime, uAspect; varying vec2 vUv;
    float h(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
    void main(){
      vec2 c = vUv - 0.5; c.x *= uAspect;
      float r = length(c);
      vec2 dir = normalize(c + 1e-5) * 0.0018 * smoothstep(0.25, 0.9, r); dir.x /= uAspect;
      vec3 col = vec3(texture2D(tDiffuse, vUv + dir).r, texture2D(tDiffuse, vUv).g, texture2D(tDiffuse, vUv - dir).b);
      col *= 1.0 - smoothstep(0.42, 1.05, r) * 0.78;          // candle-lit vignette
      col += (h(vUv * 811.0 + uTime) - 0.5) * 0.035;            // film grain
      gl_FragColor = vec4(max(col, 0.0), 1.0);
    }`,
};

type Rising = { place: string; group: THREE.Group; lines: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>; mats: THREE.Material[]; plane: THREE.Plane; base: number; height: number; at: number };
type Candle = { flame: THREE.Mesh; light: THREE.PointLight; base: number; lit: number; target: number; seed: number };

export class Film {
  readonly canvas: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(36, 1, 0.05, 60);
  private composer: EffectComposer;
  private bloom: UnrealBloomPass;
  private final: ShaderPass;
  private parchU = { uInk: { value: null as THREE.Texture | null }, uEdge: { value: 0 }, uInkK: { value: 0 }, uTime: { value: 0 } };
  private risings: Rising[] = [];
  private candles: Candle[] = [];
  private joinCandle!: Candle;
  private scroll!: { roll: THREE.Group; sheet: THREE.Mesh; seal: THREE.Group; halves: THREE.Mesh[]; draw: (s: string) => void };
  private globe!: THREE.Group;
  private sparks!: THREE.Points;
  private sparkVel!: Float32Array;
  private sparkLife!: Float32Array;
  private dust!: THREE.Points;
  private volcano!: THREE.Mesh;
  private beam!: THREE.Mesh;
  private camPos: THREE.CatmullRomCurve3;
  private camLook: THREE.CatmullRomCurve3;
  private mouse = new THREE.Vector2();
  private look = new THREE.Vector2();
  private sealBroken = false;
  private time = 0;
  p = 0;

  constructor(private readonly onProgress: (f: number) => void) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'film';
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: false, powerPreference: 'high-performance' });
    const mobile = matchMedia('(max-width: 900px)').matches;
    this.renderer.setPixelRatio(Math.min(devicePixelRatio || 1, mobile ? 1.25 : 1.6));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.shadowMap.enabled = !mobile;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.localClippingEnabled = true;
    this.scene.background = new THREE.Color(0x060504);
    this.scene.fog = new THREE.FogExp2(0x060504, 0.085);
    const pm = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pm.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.18;

    const rt = new THREE.WebGLRenderTarget(1, 1, { samples: 4, type: THREE.HalfFloatType });
    this.composer = new EffectComposer(this.renderer, rt);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.55, 0.55, 0.82);
    this.composer.addPass(this.bloom);
    this.tilt = new ShaderPass(TILT);
    this.composer.addPass(this.tilt);
    this.final = new ShaderPass(FINAL);
    this.composer.addPass(this.final);
    this.composer.addPass(new OutputPass());

    // Camera rail: one spline for where the camera is, one for what it looks at.
    const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
    // One control point per chapter, so each chapter's shot lands exactly.
    this.camPos = new THREE.CatmullRomCurve3([
      v(0.4, 4.4, 5.4), v(-0.3, 0.95, -0.15), v(-0.7, 2.45, 2.15), v(0.0, 1.05, 0.95), v(0.1, 0.62, 0.32),
      ...CARD_X.map((x) => v(x - 0.3, 1.05, 2.75)),
      v(1.75, 1.15, 0.8), v(1.75, 0.95, 2.05), v(2.3, 1.15, 2.75), v(-1.35, 0.78, 2.4), v(0.2, 5.6, 6.4),
    ], false, 'centripetal');
    this.camLook = new THREE.CatmullRomCurve3([
      v(-0.2, 0, 0), v(-0.55, 0.12, -1.65), v(-0.55, 0, -0.15), v(-0.1, 0, -0.2), v(0.22, 0.12, -0.5),
      ...CARD_X.map((x) => v(x - 0.3, 0, CARD_Z - 0.05)), // card sits right of centre, text on the left
      v(1.9, 0.0, -0.32), v(2.95, 0.5, 0.5), v(2.3, 0, 1.5), v(-2.0, 0.38, 0.95), v(-0.2, 0, 0),
    ], false, 'centripetal');

    addEventListener('pointermove', (e) => this.mouse.set((e.clientX / innerWidth) * 2 - 1, (e.clientY / innerHeight) * 2 - 1));
    addEventListener('resize', () => this.resize());
    this.resize();
  }

  // ------------------------------------------------------------------ build

  async build(): Promise<void> {
    const loader = new GLTFLoader();
    const base = import.meta.env.BASE_URL + 'models/';
    const jobs: Promise<void>[] = [];
    let done = 0, total = 0;
    const track = <T>(pr: Promise<T>) => { total++; return pr.then((r) => { done++; this.onProgress(done / total); return r; }); };

    this.buildRoom();
    this.buildMap();
    this.buildScroll();
    this.buildGlobe();
    this.buildCandles();
    this.buildSparks();
    this.buildSet();

    const fit = (o: THREE.Object3D, size: number, axis: 'x' | 'y' | 'max' = 'max') => {
      const b = new THREE.Box3().setFromObject(o), s = b.getSize(new THREE.Vector3());
      const k = size / (axis === 'max' ? Math.max(s.x, s.y, s.z) : s[axis]);
      o.scale.multiplyScalar(k);
      const b2 = new THREE.Box3().setFromObject(o);
      o.position.y -= b2.min.y;
      return o;
    };
    const shadowed = (o: THREE.Object3D) => o.traverse((m) => { if ((m as THREE.Mesh).isMesh) { m.castShadow = true; m.receiveShadow = true; } });

    // Buildings that rise out of the map.
    const towns: [string, keyof typeof PLACES, number, number, number][] = [
      ['hex/blue/building_market_blue.gltf', 'bazaar', 0, 0, 0.3],
      ['hex/red/building_home_A_red.gltf', 'bazaar', -0.2, 0.08, 0.22],
      ['hex/green/building_home_B_green.gltf', 'bazaar', 0.19, 0.1, 0.22],
      ['hex/blue/building_well_blue.gltf', 'bazaar', 0.04, -0.17, 0.2],
      ['hex/blue/building_watermill_blue.gltf', 'farms', 0, 0, 0.28],
      ['hex/green/building_archeryrange_green.gltf', 'whisper', 0.1, 0.12, 0.26],
      ['hex/blue/building_tower_B_blue.gltf', 'wharf', 0, 0, 0.26],
      ['hex/red/building_castle_red.gltf', 'keep', 0, 0, 0.36],
      ['hex/red/building_barracks_red.gltf', 'keep', 0.24, 0.06, 0.24],
      ['hex/red/building_blacksmith_red.gltf', 'mine', 0, 0, 0.27],
      ['hex/red/building_church_red.gltf', 'church', 0, 0, 0.26],
    ];
    towns.forEach(([file, place, dx, dz, size], i) => jobs.push(track(loader.loadAsync(base + file)).then((g) => {
      const o = fit(g.scene, size, 'x');
      const [u, vv] = PLACES[place];
      const pos = mapAt(u, vv).add(new THREE.Vector3(dx, 0, dz));
      o.position.x += pos.x; o.position.z += pos.z;
      o.rotation.y = (i * 1.7) % (Math.PI * 2);
      const group = new THREE.Group();
      group.add(o);
      shadowed(group);
      const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
      const mats: THREE.Material[] = [];
      const edges = new THREE.BufferGeometry();
      const pts: number[] = [];
      group.updateMatrixWorld(true);
      o.traverse((m) => {
        const mesh = m as THREE.Mesh;
        if (!mesh.isMesh) return;
        const mat = (mesh.material as THREE.Material).clone();
        mat.clippingPlanes = [plane];
        mats.push(mat);
        mesh.material = mat;
        // Wireframe: the mesh's hard edges, baked into world space.
        const e = new THREE.EdgesGeometry(mesh.geometry, 30);
        e.applyMatrix4(mesh.matrixWorld);
        pts.push(...(e.attributes.position.array as Float32Array));
      });
      edges.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
      const lines = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: new THREE.Color(1.0, 0.55, 0.22).multiplyScalar(1.15), transparent: true, opacity: 0, depthWrite: false, toneMapped: false }));
      group.add(lines);
      const box = new THREE.Box3().setFromObject(o);
      this.scene.add(group);
      this.footprints.push(box.clone());
      this.risings.push({ place, group, lines, mats, plane, base: box.min.y, height: box.max.y - box.min.y, at: C(1) + STEP * (0.45 + (i / towns.length) * 0.4) });
    })));

    // Props around the map: real models, placed like someone just left the table.
    const props: [string, number, THREE.Vector3, THREE.Euler][] = [
      ['gear/gltf/sword_2handed.gltf', 1.25, new THREE.Vector3(1.65, 0.0, -1.78), new THREE.Euler(Math.PI / 2, 0, 1.45)],
      ['gear/gltf/shield_round.gltf', 0.5, new THREE.Vector3(-2.75, 0.0, -0.75), new THREE.Euler(-Math.PI / 2, 0, 0.5)],
      ['gear/gltf/mug_full.gltf', 0.2, new THREE.Vector3(1.05, 0, 0.75), new THREE.Euler(0, 0.6, 0)],
      ['gear/gltf/spellbook_open.gltf', 0.6, new THREE.Vector3(-2.65, 0, 0.25), new THREE.Euler(0, 0.4, 0)],
      ['dungeon/gltf/coin_stack_large.gltf.glb', 0.22, new THREE.Vector3(1.35, 0, 0.45), new THREE.Euler(0, 0.3, 0)],
      ['dungeon/gltf/coin_stack_small.gltf.glb', 0.14, new THREE.Vector3(1.55, 0, 0.62), new THREE.Euler(0, 1.2, 0)],
      ['dungeon/gltf/chest_gold.glb', 0.55, new THREE.Vector3(2.7, 0, -1.25), new THREE.Euler(0, -0.6, 0)],
      ['dungeon/gltf/bottle_A_green.gltf.glb', 0.3, new THREE.Vector3(-2.5, 0, -1.25), new THREE.Euler(0, 0, 0)],
      ['dungeon/gltf/bottle_B_brown.gltf.glb', 0.26, new THREE.Vector3(-2.25, 0, -1.4), new THREE.Euler(0, 0, 0)],
      ['gear/gltf/dagger.gltf', 0.42, new THREE.Vector3(1.0, 0.0, -1.35), new THREE.Euler(Math.PI / 2, 0, -0.9)],
    ];
    props.forEach(([file, size, pos, rot]) => jobs.push(track(loader.loadAsync(base + file)).then((g) => {
      const holder = new THREE.Group();
      g.scene.rotation.copy(rot);
      holder.add(g.scene);
      fit(holder, size);
      holder.position.x = pos.x; holder.position.z = pos.z;
      shadowed(holder);
      this.scene.add(holder);
    })));
    // Tiny adventurers walking the roads to Mount Cinder, with their real walk cycles.
    (['Knight', 'Barbarian', 'Rogue'] as const).forEach((name, i) => jobs.push(track(loader.loadAsync(base + 'kaykit-adventurers/' + name + '.glb')).then((g) => {
      const obj = cloneSkinned(g.scene);
      const holder = new THREE.Group();
      holder.add(obj);
      fit(holder, 0.1, 'y');
      shadowed(holder);
      const mixer = new THREE.AnimationMixer(obj);
      const clip = g.animations.find((a) => a.name === (i === 1 ? 'Walking_B' : 'Walking_A')) ?? g.animations[0];
      mixer.clipAction(clip).play();
      holder.visible = false;
      this.scene.add(holder);
      this.walkers.push({ obj: holder, mixer, t: i * 0.27, speed: 0.022 + i * 0.004 });
    })));
    await Promise.all(jobs);
    this.routeRoad();
    // What the cursor can find on the table.
    const names: Record<string, string> = { bazaar: 'The Grand Bazaar', farms: 'Hollow Farms', whisper: 'Whisperwood', wharf: 'Lakeside Wharf', keep: 'The Old Keep', mine: 'Ironvein Mine', church: 'The Chapel', cinder: 'Mount Cinder' };
    this.risings.forEach((r) => this.hoverables.push({ obj: r.group, label: names[r.place] ?? '', kind: 'label' }));
    this.hoverables.push({ obj: this.volcano, label: 'Mount Cinder. Something is awake under it.', kind: 'label' });
    this.hoverables.push({ obj: this.d20, label: 'Roll the d20', kind: 'd20' });
    this.hoverables.push({ obj: this.globe, label: 'Drag to spin the realm', kind: 'globe' });
    this.hoverables.push({ obj: this.scroll.roll, label: 'Project 01, sealed until January', kind: 'label' });
    this.hoverables.push({ obj: this.scroll.sheet, label: 'Project 01, sealed until January', kind: 'label' });
    this.hoverables.push({ obj: this.joinCandleBody, label: 'Sign up to light this one', kind: 'label' });
    this.boxes.forEach((b) => this.hoverables.push({ obj: b.mesh, label: 'Industry standard. Flammable.', kind: 'label' }));
    this.cards.forEach((c) => this.hoverables.push({ obj: c.pivot, label: CARDS[c.i].title, kind: 'label' }));
    this.hoverables.push({ obj: this.letter, label: 'A letter from the founder', kind: 'label' });
    this.onProgress(1);
    // Compile everything up front so the first scroll never hitches.
    await this.renderer.compileAsync(this.scene, this.camera);
  }

  private buildRoom(): void {
    // The table: a heavy plank top on thick legs, in the dark.
    const wood = woodTexture();
    wood.repeat.set(1.5, 1);
    const top = new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.2, 4.2), new THREE.MeshStandardMaterial({ map: wood, roughness: 0.62, metalness: 0.05, bumpMap: wood, bumpScale: 0.6 }));
    top.position.y = -0.1;
    top.receiveShadow = true;
    this.scene.add(top);
    const legMat = new THREE.MeshStandardMaterial({ color: 0x22150c, roughness: 0.7 });
    for (const [x, z] of [[-3.3, -1.8], [3.3, -1.8], [-3.3, 1.8], [3.3, 1.8]]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.28, 3, 0.28), legMat);
      leg.position.set(x, -1.7, z);
      this.scene.add(leg);
    }
    // Lights: low warm ambience, a cold moon through a window (the only shadow caster), candles.
    this.scene.add(new THREE.HemisphereLight(0x3a2c22, 0x0a0705, 0.35));
    const moon = new THREE.DirectionalLight(0x8aa2d8, 1.1);
    moon.position.set(-4.5, 7, -4);
    moon.target.position.set(0, 0, 0.3);
    moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    Object.assign(moon.shadow.camera, { left: -4.5, right: 4.5, top: 3.5, bottom: -3.5, near: 1, far: 18 });
    moon.shadow.bias = -0.0004;
    moon.shadow.radius = 4;
    this.scene.add(moon, moon.target);
    // The beam the moonlight makes in the dusty air.
    const cone = new THREE.CylinderGeometry(0.35, 1.9, 8.2, 48, 1, true);
    cone.translate(0, -4.1, 0);
    this.beam = new THREE.Mesh(cone, new THREE.ShaderMaterial({ vertexShader: BEAM_VS, fragmentShader: BEAM_FS, uniforms: { uTime: { value: 0 } }, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
    this.beam.position.copy(moon.position).multiplyScalar(0.62);
    this.beam.lookAt(0, 0, 0.3);
    this.beam.rotateX(Math.PI / 2);
    this.scene.add(this.beam);
    // Dust motes drifting in the beam.
    const n = 600, pos = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const t = Math.random();
      const p = new THREE.Vector3().lerpVectors(this.beam.position, new THREE.Vector3(0, 0.2, 0.3), t);
      p.add(new THREE.Vector3().randomDirection().multiplyScalar(0.3 + t * 0.9));
      pos.set([p.x, p.y, p.z], i * 3);
    }
    const dg = new THREE.BufferGeometry();
    dg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.dust = new THREE.Points(dg, new THREE.PointsMaterial({ color: 0xbfcfff, size: 0.012, transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending }));
    this.scene.add(this.dust);
  }

  private buildMap(): void {
    const [blank, inked] = mapTextures();
    // A real lit material (candles, moon, shadows), with the burn-in and the self-drawing
    // ink spliced into its shader.
    this.parchU.uInk.value = inked;
    const parchment = new THREE.MeshStandardMaterial({ map: blank, roughness: 0.92, metalness: 0 });
    parchment.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this.parchU);
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\n' + PARCHMENT_HEAD)
        .replace('#include <map_fragment>', PARCHMENT_MAP)
        .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n totalEmissiveRadiance += vec3(1.0, 0.45, 0.12) * pGlow * (0.8 + 0.4 * sin(uTime * 7.0 + vMapUv.x * 40.0)) * 3.0;');
    };
    // Slightly curled sheet: the corners lift off the table.
    const geo = new THREE.PlaneGeometry(MAP.w, MAP.d, 64, 44);
    geo.rotateX(-Math.PI / 2);
    const pa = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pa.count; i++) {
      const x = pa.getX(i) / (MAP.w / 2), z = pa.getZ(i) / (MAP.d / 2);
      pa.setY(i, Math.pow(Math.max(Math.abs(x), Math.abs(z)), 6) * 0.035 + 0.003);
    }
    geo.computeVertexNormals();
    const map = new THREE.Mesh(geo, parchment);
    map.position.set(MAP.cx, 0, MAP.cz);
    map.receiveShadow = true;
    this.scene.add(map);
    // Mount Cinder: a small ink-black volcano with a lava throat, rising with the towns.
    const v = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.26, 7, 1), new THREE.MeshStandardMaterial({ color: 0x2a1d17, roughness: 0.9, flatShading: true }));
    const lava = new THREE.Mesh(new THREE.CircleGeometry(0.045, 16), new THREE.MeshBasicMaterial({ color: new THREE.Color(1, 0.35, 0.08).multiplyScalar(4), toneMapped: false }));
    lava.rotation.x = -Math.PI / 2;
    lava.position.y = 0.105;
    v.add(lava);
    const at = mapAt(PLACES.cinder[0], PLACES.cinder[1]);
    v.position.set(at.x, 0.13, at.z);
    v.castShadow = true;
    this.volcano = v;
    this.scene.add(v);
    // The road the adventurers walk: bazaar, keep, mine, then the Dragon Road up the mountain.
    this.road = new THREE.CatmullRomCurve3((['bazaar', 'keep', 'mine', 'cinder'] as const).map((k) => mapAt(PLACES[k][0], PLACES[k][1], 0.005)), false, 'centripetal');
    // Smoke from the crater.
    const sn = 160, sp = new Float32Array(sn * 3).fill(-99);
    const sg = new THREE.BufferGeometry();
    sg.setAttribute('position', new THREE.BufferAttribute(sp, 3));
    this.smokeAge = new Float32Array(sn).map(() => Math.random() * 4);
    this.smoke = new THREE.Points(sg, new THREE.PointsMaterial({ color: 0x8a8580, size: 0.06, transparent: true, opacity: 0.28, depthWrite: false }));
    this.smoke.frustumCulled = false;
    this.scene.add(this.smoke);
    // Inkpot and quill beside the map.
    const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 0.14, 24), new THREE.MeshPhysicalMaterial({ color: 0x0b0b10, roughness: 0.15, clearcoat: 1 }));
    pot.position.set(-2.35, 0.07, 0.95);
    pot.castShadow = true;
    const feather = new THREE.Shape();
    feather.moveTo(0, 0); feather.quadraticCurveTo(0.07, 0.25, 0, 0.62); feather.quadraticCurveTo(-0.05, 0.3, 0, 0);
    const quill = this.quill = new THREE.Mesh(new THREE.ShapeGeometry(feather, 16), new THREE.MeshStandardMaterial({ color: 0xece6da, roughness: 0.8, side: THREE.DoubleSide }));
    quill.position.set(-2.35, 0.1, 0.95);
    quill.rotation.set(-0.35, 0.6, -0.4);
    quill.castShadow = true;
    // A d20, because every map table has one.
    const d20 = this.d20 = new THREE.Mesh(new THREE.IcosahedronGeometry(0.09, 0), new THREE.MeshPhysicalMaterial({ color: 0x7a1010, roughness: 0.25, clearcoat: 0.8, flatShading: true }));
    d20.position.set(1.78, 0.075, 1.95);
    d20.rotation.set(0.4, 0.7, 0.1);
    d20.castShadow = true;
    this.scene.add(pot, quill, d20);
    this.quillHome.copy(quill.position);
    this.quillRot.copy(quill.rotation);
    this.d20Home.copy(d20.position);
  }

  private buildScroll(): void {
    const { tex, draw } = scrollTexture();
    const paper = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.85, side: THREE.DoubleSide });
    const rollMat = new THREE.MeshStandardMaterial({ color: 0xd9c296, roughness: 0.85 });
    const roll = new THREE.Group();
    const cyl = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.065, 1.05, 32), rollMat);
    cyl.rotation.z = Math.PI / 2;
    cyl.castShadow = true;
    roll.add(cyl);
    for (const s of [-1, 1]) { // wooden end knobs
      const k = new THREE.Mesh(new THREE.SphereGeometry(0.045, 16, 12), new THREE.MeshStandardMaterial({ color: 0x4a2c18, roughness: 0.5 }));
      k.position.x = s * 0.56;
      roll.add(k);
    }
    // The wax seal: a red disc pressed with a flame, in two halves so it can break.
    const seal = new THREE.Group();
    const halves: THREE.Mesh[] = [];
    const waxMat = new THREE.MeshPhysicalMaterial({ color: 0x8e1410, roughness: 0.32, clearcoat: 0.6, emissive: 0x3a0402 });
    for (const s of [0, 1]) {
      const half = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.08, 0.02, 24, 1, false, s * Math.PI, Math.PI), waxMat);
      half.rotation.x = Math.PI / 2;
      half.castShadow = true;
      halves.push(half);
      seal.add(half);
    }
    seal.position.set(0, 0, 0.068);
    roll.add(seal);
    roll.position.set(1.95, 0.065, -0.6);
    // The unrolled sheet grows toward the camera from under the roll.
    const sheet = new THREE.Mesh(new THREE.PlaneGeometry(1.0, 0.7), paper);
    sheet.rotation.x = -Math.PI / 2;
    sheet.position.set(1.95, 0.004, -0.6);
    sheet.receiveShadow = true;
    sheet.scale.set(1, 0.001, 1);
    this.scene.add(roll, sheet);
    this.scroll = { roll, sheet, seal, halves, draw };
  }

  private buildGlobe(): void {
    const brass = new THREE.MeshStandardMaterial({ color: 0xb08a3e, metalness: 1, roughness: 0.32 });
    const g = new THREE.Group();
    const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.22, 0.06, 32), brass);
    foot.position.y = 0.03;
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.035, 0.32, 16), brass);
    post.position.y = 0.2;
    const tilt = new THREE.Group();
    tilt.position.y = 0.66;
    tilt.rotation.z = 0.41;
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.3, 64, 48), new THREE.MeshStandardMaterial({ map: globeTexture(), roughness: 0.55 }));
    ball.name = 'ball';
    const meridian = new THREE.Mesh(new THREE.TorusGeometry(0.335, 0.012, 12, 96), brass);
    meridian.rotation.y = Math.PI / 2;
    const horizon = new THREE.Mesh(new THREE.TorusGeometry(0.345, 0.014, 12, 96), brass);
    horizon.rotation.x = Math.PI / 2;
    horizon.position.y = 0.66;
    tilt.add(ball, meridian);
    g.add(foot, post, tilt, horizon);
    g.traverse((m) => { if ((m as THREE.Mesh).isMesh) m.castShadow = true; });
    g.position.set(2.85, 0, 0.55);
    this.globe = g;
    this.scene.add(g);
  }

  private buildCandles(): void {
    const flameGeo = new THREE.PlaneGeometry(1, 1);
    const waxMat = new THREE.MeshStandardMaterial({ color: 0xe8dcc4, roughness: 0.5, emissive: 0x3a1e08, emissiveIntensity: 0.4 });
    const make = (x: number, z: number, h: number, lit: boolean, seed: number): Candle => {
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.065, h, 24), waxMat);
      body.position.set(x, h / 2, z);
      body.castShadow = true;
      const holder = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 0.03, 24), new THREE.MeshStandardMaterial({ color: 0x6b4a22, metalness: 0.9, roughness: 0.35 }));
      holder.position.set(x, 0.015, z);
      const wick = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.004, 0.035, 6), new THREE.MeshBasicMaterial({ color: 0x111111 }));
      wick.position.set(x, h + 0.015, z);
      const flame = new THREE.Mesh(flameGeo, new THREE.ShaderMaterial({
        vertexShader: FLAME_VS, fragmentShader: FLAME_FS, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
        uniforms: { uTime: { value: 0 }, uSeed: { value: seed }, uLit: { value: lit ? 1 : 0 } },
      }));
      flame.position.set(x, h + 0.075, z);
      flame.scale.set(0.09, 0.16, 1);
      const light = new THREE.PointLight(0xffa25a, lit ? 1.6 : 0, 4.5, 1.7);
      light.position.set(x, h + 0.12, z);
      this.scene.add(body, holder, wick, flame, light);
      const c: Candle = { flame, light, base: 1.6, lit: lit ? 1 : 0, target: lit ? 1 : 0, seed };
      this.lastCandleBody = body;
      this.candles.push(c);
      return c;
    };
    make(-2.45, -0.9, 0.42, true, 0.1);
    make(-2.2, -1.15, 0.3, true, 0.5);
    make(2.65, -0.25, 0.36, true, 0.8);
    make(0.75, -1.45, 0.26, true, 0.3);
    this.joinCandle = make(-2.4, 1.05, 0.5, false, 0.66);
    this.joinCandleBody = this.lastCandleBody;
    this.joinCandle.base = 2.6;
  }

  /** The industry's junk, stacked at the back of the table. It burns away as you scroll past. */
  private boxes: { mesh: THREE.Mesh; u: { uBurn: { value: number }; uSeed: { value: number } } }[] = [];
  private cards: { pivot: THREE.Group; i: number }[] = [];
  private letter!: THREE.Mesh;

  private buildSet(): void {
    const junk: [string, number, number, number, number][] = [
      ['LOOT BOX', -0.95, 0.24, -1.62, 0.2], ['SEASON PASS', -0.4, 0.2, -1.7, -0.25], ['ALWAYS ONLINE', -0.7, 0.18, -1.66, 0.1],
    ];
    junk.forEach(([text, x, size, z, rot], i) => {
      const u = { uBurn: { value: 0 }, uSeed: { value: i * 3.7 } };
      const mat = new THREE.MeshStandardMaterial({ map: boxTexture(text), roughness: 0.95 });
      mat.onBeforeCompile = (sh) => {
        Object.assign(sh.uniforms, u);
        sh.fragmentShader = sh.fragmentShader
          .replace('#include <common>', '#include <common>\nuniform float uBurn, uSeed; float bh(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); } float bn(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f); return mix(mix(bh(i), bh(i+vec2(1,0)), f.x), mix(bh(i+vec2(0,1)), bh(i+vec2(1,1)), f.x), f.y); } float bEdge = 0.0;')
          .replace('#include <map_fragment>', '#include <map_fragment>\n float bf = bn(vMapUv * 5.0 + uSeed) * 0.6 + bn(vMapUv * 13.0 - uSeed) * 0.4; float thr = uBurn * 1.25 - 0.1; if (bf < thr) discard; bEdge = smoothstep(thr + 0.08, thr, bf); diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.08, 0.05, 0.03), smoothstep(thr + 0.2, thr, bf));')
          .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n totalEmissiveRadiance += vec3(1.0, 0.42, 0.1) * bEdge * 3.0;');
      };
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(size * 1.5, size, size), mat);
      mesh.position.set(x, i === 2 ? 0.2 + size / 2 : size / 2, z);
      mesh.rotation.y = rot;
      mesh.castShadow = true; mesh.receiveShadow = true;
      this.scene.add(mesh);
      this.boxes.push({ mesh, u });
    });
    // What we make: five cards dealt face down in a row; each turns over as you reach it.
    const { fronts, back } = cardTextures();
    CARDS.forEach((_, i) => {
      const pivot = new THREE.Group();
      const geo = new THREE.PlaneGeometry(0.42, 0.63);
      const front = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ map: fronts[i], roughness: 0.85 }));
      front.rotation.x = -Math.PI / 2;
      const backM = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ map: back, roughness: 0.85 }));
      backM.rotation.x = Math.PI / 2;
      backM.position.y = -0.001;
      front.receiveShadow = backM.receiveShadow = true;
      front.castShadow = backM.castShadow = true;
      pivot.add(front, backM);
      pivot.position.set(CARD_X[i], 0.006, CARD_Z);
      pivot.rotation.y = (i - 2) * 0.04;
      this.scene.add(pivot);
      this.cards.push({ pivot, i });
    });
    // The founder's letter, pinned under the globe's shadow.
    this.letter = new THREE.Mesh(new THREE.PlaneGeometry(0.72, 0.93), new THREE.MeshStandardMaterial({ map: letterTexture(), roughness: 0.9 }));
    this.letter.rotation.set(-Math.PI / 2, 0, -0.06);
    this.letter.position.set(2.3, 0.005, 1.5);
    this.letter.receiveShadow = true;
    this.scene.add(this.letter);
  }

  private buildSparks(): void {
    // Sparks: rise from lit candles, burst from the broken seal and the join candle.
    const n = 900;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3).fill(-99), 3));
    this.sparkVel = new Float32Array(n * 3);
    this.sparkLife = new Float32Array(n);
    this.sparks = new THREE.Points(g, new THREE.PointsMaterial({ color: new THREE.Color(1, 0.55, 0.2).multiplyScalar(3), size: 0.014, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
    this.sparks.frustumCulled = false;
    this.scene.add(this.sparks);
  }

  private emit(at: THREE.Vector3, count: number, speed: number, spread: number): void {
    const pa = this.sparks.geometry.attributes.position as THREE.BufferAttribute;
    for (let k = 0, i = 0; i < this.sparkLife.length && k < count; i++) {
      if (this.sparkLife[i] > 0) continue;
      this.sparkLife[i] = 0.6 + Math.random() * 1.2;
      pa.setXYZ(i, at.x + (Math.random() - 0.5) * spread, at.y, at.z + (Math.random() - 0.5) * spread);
      const d = new THREE.Vector3((Math.random() - 0.5) * 0.6, 0.6 + Math.random(), (Math.random() - 0.5) * 0.6).normalize().multiplyScalar(speed * (0.4 + Math.random()));
      this.sparkVel.set([d.x, d.y, d.z], i * 3);
      k++;
    }
  }

  // ------------------------------------------------------------------ public

  /** Pointer moved: x, y in client pixels. */
  pointer(x: number, y: number, dx: number): void {
    this.ndc.set((x / innerWidth) * 2 - 1, -(y / innerHeight) * 2 + 1);
    this.pointerDirty = true;
    if (this.dragging) this.globeVel += dx * 0.012;
  }
  pointerDown(): boolean {
    if (this.hovered?.kind === 'globe') { this.dragging = true; return true; }
    if (this.hovered?.kind === 'd20' && this.roll.t >= 1) {
      this.roll = { t: 0, from: this.d20.quaternion.clone(), to: new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28)), n: 1 + Math.floor(Math.random() * 20) };
      this.onCue('roll');
      return true;
    }
    return false;
  }
  pointerUp(): void { this.dragging = false; }
  get hovering(): string | null { return this.hovered?.kind ?? null; }

  /** Sign-up succeeded: the cold candle catches. */
  ignite(): void {
    this.joinCandle.target = 1;
    this.emit(new THREE.Vector3(-2.4, 0.58, 1.05), 160, 1.2, 0.05);
    this.flashT = 1;
    this.onCue('ignite');
  }
  setJoined(): void { this.joinCandle.target = 1; this.joinCandle.lit = 1; }
  private flashT = 0;
  private tilt!: ShaderPass;
  private lastCandleBody!: THREE.Mesh;
  private joinCandleBody!: THREE.Mesh;
  private walkers: { obj: THREE.Object3D; mixer: THREE.AnimationMixer; t: number; speed: number; yaw?: number }[] = [];
  private footprints: THREE.Box3[] = [];

  /** Re-trace the walking road so it skirts every building (and the mountain) instead of
   *  passing through them: sample the inked road, push each point out of every footprint,
   *  relax, repeat, and finish on a push so nothing is left inside. */
  private routeRoad(): void {
    const N = 240, CLEAR = 0.045;
    const obs = this.footprints.map((b) => {
      const c = b.getCenter(new THREE.Vector3()), sz = b.getSize(new THREE.Vector3());
      return { x: c.x, z: c.z, r: Math.max(sz.x, sz.z) * 0.5 + CLEAR }; // hex tiles: half the width reaches the corners
    });
    obs.push({ x: this.volcano.position.x, z: this.volcano.position.z, r: 0.2 + CLEAR });
    // The inked road starts in the middle of the bazaar and ends in the crater. Walk only the
    // stretch between the edge of town and the foot of the mountain.
    const inside = (q: THREE.Vector3) => obs.some((o) => Math.hypot(q.x - o.x, q.z - o.z) < o.r);
    const raw = this.road.getSpacedPoints(N);
    let a = 0, b = N;
    while (a < b && inside(raw[a])) a++;
    while (b > a && inside(raw[b])) b--;
    // Find the walk with a grid search: cells inside a footprint are walls, and every step
    // costs more the further it strays from the ink, so the path hugs the drawn road and
    // goes around buildings instead of squeezing between them.
    const G = 0.012, x0 = MAP.cx - MAP.w / 2, z0 = MAP.cz - MAP.d / 2;
    const W = Math.ceil(MAP.w / G), H = Math.ceil(MAP.d / G);
    const ink = raw.slice(a, b + 1);
    const cost = new Float32Array(W * H);
    for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
      const x = x0 + (i + 0.5) * G, z = z0 + (j + 0.5) * G;
      let wall = false, near = 9;
      for (const o of obs) { const d = Math.hypot(x - o.x, z - o.z) - o.r; if (d < 0) wall = true; near = Math.min(near, d); }
      let dr = 9;
      for (const q of ink) dr = Math.min(dr, Math.hypot(x - q.x, z - q.z));
      cost[j * W + i] = wall ? Infinity : 1 + 40 * Math.min(dr, 0.4) + (near < 0.04 ? (0.04 - near) * 60 : 0);
    }
    const cell = (q: THREE.Vector3) => Math.min(H - 1, Math.max(0, Math.floor((q.z - z0) / G))) * W + Math.min(W - 1, Math.max(0, Math.floor((q.x - x0) / G)));
    const start = cell(ink[0]), goal = cell(ink[ink.length - 1]);
    const dist = new Float32Array(W * H).fill(Infinity), from = new Int32Array(W * H).fill(-1);
    const heap: [number, number][] = [];
    const hpush = (n: [number, number]) => { heap.push(n); let k = heap.length - 1; while (k > 0) { const up = (k - 1) >> 1; if (heap[up][0] <= heap[k][0]) break; [heap[up], heap[k]] = [heap[k], heap[up]]; k = up; } };
    const hpop = () => { const top = heap[0], last = heap.pop()!; if (heap.length) { heap[0] = last; let k = 0; for (;;) { const l = 2 * k + 1, r = l + 1; let m = k; if (l < heap.length && heap[l][0] < heap[m][0]) m = l; if (r < heap.length && heap[r][0] < heap[m][0]) m = r; if (m === k) break; [heap[m], heap[k]] = [heap[k], heap[m]]; k = m; } } return top; };
    dist[start] = 0; hpush([0, start]);
    const gi = goal % W, gj = (goal / W) | 0;
    while (heap.length) {
      const [, c] = hpop();
      if (c === goal) break;
      const ci = c % W, cj = (c / W) | 0;
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        if (!di && !dj) continue;
        const ni = ci + di, nj = cj + dj;
        if (ni < 0 || nj < 0 || ni >= W || nj >= H) continue;
        const n = nj * W + ni;
        if (cost[n] === Infinity) continue;
        const nd = dist[c] + Math.hypot(di, dj) * (cost[c] + cost[n]) * 0.5;
        if (nd < dist[n]) { dist[n] = nd; from[n] = c; hpush([nd + Math.hypot(ni - gi, nj - gj), n]); }
      }
    }
    const route: THREE.Vector3[] = [];
    for (let c = goal; c !== -1; c = from[c]) route.unshift(new THREE.Vector3(x0 + ((c % W) + 0.5) * G, ink[0].y, z0 + (((c / W) | 0) + 0.5) * G));
    if (route.length < 2) return; // no way through: keep the inked road rather than nothing
    const pts = new THREE.CatmullRomCurve3(route, false, 'centripetal').getSpacedPoints(N);
    // Iron out the grid's stair-steps, then make sure smoothing didn't cut a corner.
    for (let it = 0; it < 30; it++) {
      for (let i = 1; i < N; i++) {
        pts[i].x = pts[i].x * 0.5 + (pts[i - 1].x + pts[i + 1].x) * 0.25;
        pts[i].z = pts[i].z * 0.5 + (pts[i - 1].z + pts[i + 1].z) * 0.25;
      }
      for (const q of pts) for (const o of obs) {
        const dx = q.x - o.x, dz = q.z - o.z, d = Math.hypot(dx, dz);
        if (d < o.r && d > 1e-5) { q.x = o.x + (dx / d) * o.r; q.z = o.z + (dz / d) * o.r; }
      }
    }
    this.road = new THREE.CatmullRomCurve3(pts, false, 'centripetal');
  }
  private road!: THREE.CatmullRomCurve3;
  private smoke!: THREE.Points;
  private smokeAge!: Float32Array;
  private quill!: THREE.Mesh;
  private readonly quillHome = new THREE.Vector3();
  private readonly quillRot = new THREE.Euler();
  private d20!: THREE.Mesh;
  private readonly d20Home = new THREE.Vector3();
  private roll = { t: 1, from: new THREE.Quaternion(), to: new THREE.Quaternion(), n: 20 };
  private globeAngle = 0;
  private globeVel = 0;
  private dragging = false;
  private hoverables: { obj: THREE.Object3D; label: string; kind: 'label' | 'd20' | 'globe' }[] = [];
  private hovered: { obj: THREE.Object3D; label: string; kind: 'label' | 'd20' | 'globe' } | null = null;
  private readonly ray = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2(9, 9);
  private pointerDirty = false;
  private burnCued = false;
  /** Sound cues: 'burn' (map catches), 'seal' (wax breaks), 'ignite' (join candle), 'roll' (d20). */
  onCue: (cue: 'burn' | 'seal' | 'ignite' | 'roll' | 'land') => void = () => {};
  /** Hover label for the cursor tooltip; null when nothing is under it. */
  onHover: (label: string | null) => void = () => {};
  onRoll: (n: number) => void = () => {};

  setCountdown(s: string): void { this.scroll?.draw(s); }

  resize(): void {
    const w = innerWidth, h = innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    // Narrow screens: widen the lens so the table still reads.
    this.camera.fov = w / h < 1 ? 52 : 36;
    this.camera.updateProjectionMatrix();
    this.composer.setSize(w, h);
    this.bloom.setSize(w, h);
    this.final.uniforms.uAspect.value = w / h;
    this.tilt.uniforms.uRes.value.set(w * this.renderer.getPixelRatio(), h * this.renderer.getPixelRatio());
  }

  // ------------------------------------------------------------------ the living table

  private updateLife(dt: number, t: number, p: number): void {
    // Adventurers walk once the roads are inked.
    const roadsOn = p > C(2) - STEP * 0.2 && p < C(10);
    for (const w of this.walkers) {
      w.obj.visible = roadsOn;
      if (!roadsOn) continue;
      // Out to the foot of the mountain and back again, turning around rather than vanishing.
      w.t = (w.t + w.speed * dt) % 2;
      const back = w.t > 1, u = back ? 2 - w.t : w.t;
      const at = this.road.getPointAt(THREE.MathUtils.clamp(u, 0, 1));
      const ahead = this.road.getPointAt(THREE.MathUtils.clamp(u + (back ? -0.01 : 0.01), 0, 1));
      w.obj.position.copy(at);
      const want = Math.atan2(ahead.x - at.x, ahead.z - at.z);
      if (w.yaw === undefined) w.yaw = want;
      let dy = want - w.yaw;
      dy = Math.atan2(Math.sin(dy), Math.cos(dy));
      w.yaw += dy * Math.min(1, dt * 5);
      w.obj.rotation.set(0, w.yaw, 0);
      w.mixer.update(dt);
    }
    // Smoke rises from the crater once the mountain is up.
    const sa = this.smoke.geometry.attributes.position as THREE.BufferAttribute;
    const on = this.volcano.scale.x > 0.5;
    const top = this.volcano.position.clone().add(new THREE.Vector3(0, 0.13 * this.volcano.scale.y, 0));
    for (let i = 0; i < this.smokeAge.length; i++) {
      this.smokeAge[i] += dt;
      if (this.smokeAge[i] > 4) { this.smokeAge[i] = 0; if (on) sa.setXYZ(i, top.x, top.y, top.z); else sa.setXYZ(i, 0, -99, 0); continue; }
      const k = this.smokeAge[i];
      sa.setXYZ(i, sa.getX(i) + (0.012 + Math.sin(t * 0.7 + i) * 0.01) * dt, sa.getY(i) + 0.07 * dt, sa.getZ(i) + Math.cos(t * 0.5 + i) * 0.008 * dt * (1 + k));
    }
    sa.needsUpdate = true;
    // The quill skims the ink front while the map draws itself, then goes back to the pot.
    const inkK = this.parchU.uInkK.value;
    const writing = inkK > 0.02 && inkK < 0.98 ? 1 : 0;
    const r = (inkK * 1.4 - 0.2) / 1.25, a = t * 2.4;
    const qu = THREE.MathUtils.clamp(0.45 + Math.cos(a) * r, 0.08, 0.92), qv = THREE.MathUtils.clamp(0.5 + Math.sin(a) * r * 0.9, 0.1, 0.9);
    const nib = mapAt(qu, qv, 0.03 + Math.abs(Math.sin(t * 9)) * 0.012);
    this.quill.position.lerp(writing ? nib : this.quillHome, 1 - Math.exp(-dt * (writing ? 8 : 3)));
    this.quill.rotation.set(this.quillRot.x * (1 - writing) - 0.9 * writing, this.quillRot.y + writing * a * 0.15, this.quillRot.z);
    // The d20: a tumble and a hop, then it lands on a number.
    if (this.roll.t < 1) {
      this.roll.t = Math.min(1, this.roll.t + dt / 0.9);
      const k = this.roll.t, spin = new THREE.Quaternion().setFromEuler(new THREE.Euler(k * 14, k * 9, k * 6));
      this.d20.quaternion.slerpQuaternions(this.roll.from, this.roll.to, ease(k)).multiply(spin.slerp(new THREE.Quaternion(), ease(k)));
      this.d20.position.set(this.d20Home.x + Math.sin(k * 9) * 0.03 * (1 - k), this.d20Home.y + Math.abs(Math.sin(k * Math.PI * 2.5)) * 0.14 * (1 - k), this.d20Home.z);
      if (this.roll.t >= 1) { this.onRoll(this.roll.n); this.onCue('land'); }
    }
    // Hover: what's under the cursor, if anything.
    if (this.pointerDirty) {
      this.pointerDirty = false;
      this.ray.setFromCamera(this.ndc, this.camera);
      let best: typeof this.hovered = null, bestD = Infinity;
      for (const h of this.hoverables) {
        if (!h.obj.visible) continue;
        // Buildings only count once they've risen out of the paper.
        const rising = this.risings.find((r) => r.group === h.obj);
        if (rising && rising.plane.constant < rising.base + rising.height * 0.6) continue;
        if (h.obj === this.volcano && this.volcano.scale.x < 0.6) continue;
        const hit = this.ray.intersectObject(h.obj, true)[0];
        if (hit && hit.distance < bestD) { bestD = hit.distance; best = h; }
      }
      if (best !== this.hovered) {
        this.hovered = best;
        this.onHover(best ? best.label : null);
      }
    }
    // Tilt-shift on the map shot only.
    const tk = Math.max(0, 1 - Math.abs(p - C(2)) / (STEP * 0.7), 1 - Math.abs(p - C(3)) / (STEP * 0.7)) * 0.9;
    this.tilt.enabled = tk > 0.01;
    this.tilt.uniforms.uK.value = ease(tk);
  }

  private updateSet(dt: number, t: number, p: number): void {
    // The junk burns as you scroll away from it.
    this.boxes.forEach((b, i) => {
      const k = band(p, C(1) + STEP * (0.1 + i * 0.12), C(1) + STEP * (0.75 + i * 0.1));
      b.u.uBurn.value = k;
      b.mesh.visible = k < 0.999;
      if (k > 0.05 && k < 0.9 && Math.random() < dt * 25) this.emit(b.mesh.position.clone().add(new THREE.Vector3((Math.random() - 0.5) * 0.3, 0.1, 0)), 1, 0.5, 0.2);
    });
    // Cards turn over one by one as the camera reaches them.
    for (const c of this.cards) {
      const k = ease(band(p, C(5 + c.i) - STEP * 0.55, C(5 + c.i) - STEP * 0.05));
      c.pivot.rotation.z = Math.PI * (1 - k);
      c.pivot.position.y = 0.006 + Math.sin(k * Math.PI) * 0.16;
    }
    // Mount Cinder grumbles while you look at it.
    const cinder = Math.max(0, 1 - Math.abs(p - C(4)) / (STEP * 0.8));
    if (cinder > 0.3 && Math.random() < dt * 30 * cinder) this.emit(this.volcano.position.clone().add(new THREE.Vector3(0, 0.12, 0)), 1, 0.7, 0.03);
    (this.volcano.children[0] as THREE.Mesh).scale.setScalar(0.9 + Math.sin(t * 3) * 0.1 + cinder * 0.35);
    // Finale: every candle flares.
    const fin = band(p, C(13) + STEP * 0.3, C(14));
    for (const c of this.candles) if (c !== this.joinCandle) c.base = 1.6 + fin * 1.6;
  }

  // ------------------------------------------------------------------ frame

  update(dt: number): void {
    this.time += dt;
    const t = this.time, p = this.p;
    this.look.lerp(this.mouse, 1 - Math.exp(-dt * 2));

    // Chapter 0 -> 1: the map's edge burns in, then the ink draws itself.
    this.parchU.uEdge.value = ease(band(p, C(1) + STEP * 0.15, C(1) + STEP * 0.7));
    if (p > C(1) + STEP * 0.15 && !this.burnCued) { this.burnCued = true; this.onCue('burn'); }
    if (p < C(1)) this.burnCued = false;
    this.parchU.uInkK.value = ease(band(p, C(1) + STEP * 0.4, C(2)));
    this.parchU.uTime.value = t;
    for (const r of this.risings) {
      const k = band(p, r.at, r.at + STEP * 0.5);
      r.lines.material.opacity = Math.min(1, k * 4) * (1 - band(p, r.at + STEP * 0.3, r.at + STEP * 1.1)) * 0.9 + 0.06 * Math.min(1, k * 4);
      r.plane.constant = r.base + r.height * ease(band(p, r.at + STEP * 0.15, r.at + STEP * 0.8)) - 0.0005;
    }
    this.volcano.scale.setScalar(Math.max(0.001, ease(band(p, C(2) - STEP * 0.3, C(2)))));
    (this.volcano.children[0] as THREE.Mesh).scale.setScalar(0.9 + Math.sin(t * 3) * 0.1);

    // Chapter 2: the seal bursts, the scroll unrolls toward you.
    const unroll = ease(band(p, C(10) - STEP * 0.6, C(10)));
    const sc = this.scroll;
    const broke = p > C(10) - STEP * 0.65;
    if (broke && !this.sealBroken) { this.sealBroken = true; this.onCue('seal'); this.emit(sc.roll.position.clone().add(new THREE.Vector3(0, 0.1, 0.06)), 120, 0.9, 0.08); }
    if (!broke) this.sealBroken = false;
    sc.halves.forEach((hf, i) => {
      const s = i ? 1 : -1, k = ease(band(p, C(10) - STEP * 0.65, C(10) - STEP * 0.3));
      hf.position.set(s * k * 0.12, -k * 0.06, k * 0.05);
      hf.rotation.y = s * k * 1.2;
    });
    sc.seal.visible = unroll < 0.98;
    sc.roll.position.z = -0.6 + unroll * 0.68;
    sc.roll.rotation.x = unroll * 10;
    sc.sheet.scale.y = Math.max(0.001, unroll);
    sc.sheet.position.z = -0.6 + unroll * 0.34;

    // Chapter 3: the globe turns.
    const ball = this.globe.getObjectByName('ball')!;
    this.globeVel *= Math.exp(-dt * 1.4);
    this.globeAngle += dt * 0.25 + this.globeVel * dt;
    ball.rotation.y = this.globeAngle + p * 6;

    // Candles flicker; the join candle catches when you sign up.
    for (const c of this.candles) {
      c.lit += (c.target - c.lit) * (1 - Math.exp(-dt * 3));
      const fl = 0.85 + Math.sin(t * 13 + c.seed * 40) * 0.06 + Math.sin(t * 29 + c.seed * 9) * 0.05;
      c.light.intensity = c.base * c.lit * fl;
      const u = (c.flame.material as THREE.ShaderMaterial).uniforms;
      u.uTime.value = t;
      u.uLit.value = c.lit;
      c.flame.scale.set(0.09 * (0.6 + c.lit * 0.4), 0.16 * c.lit * fl, 1);
      if (c.lit > 0.5 && Math.random() < dt * 3) this.emit(c.light.position.clone(), 1, 0.25, 0.01);
    }
    this.flashT = Math.max(0, this.flashT - dt * 0.8);
    this.bloom.strength = 0.55 + this.flashT * 1.6;

    // Sparks: rise, slow, cool.
    const pa = this.sparks.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < this.sparkLife.length; i++) {
      if (this.sparkLife[i] <= 0) continue;
      this.sparkLife[i] -= dt;
      const v = this.sparkVel;
      v[i * 3 + 1] += dt * 0.15;
      v[i * 3] *= 0.98; v[i * 3 + 2] *= 0.98;
      pa.setXYZ(i, pa.getX(i) + v[i * 3] * dt + Math.sin(t * 3 + i) * 0.0008, pa.getY(i) + v[i * 3 + 1] * dt, pa.getZ(i) + v[i * 3 + 2] * dt);
      if (this.sparkLife[i] <= 0) pa.setXYZ(i, 0, -99, 0);
    }
    pa.needsUpdate = true;
    this.dust.rotation.y = Math.sin(t * 0.05) * 0.02;
    (this.beam.material as THREE.ShaderMaterial).uniforms.uTime.value = t;

    // Camera on the rail, with a little hand-held drift and mouse parallax.
    const u = THREE.MathUtils.clamp(p, 0, 1);
    // Map p to the curve's parameter piecewise, chapter to chapter (eased within each leg).
    let i = 0;
    while (i < CHAPTERS.length - 2 && u > CHAPTERS[i + 1]) i++;
    const leg = ease(band(u, CHAPTERS[i], CHAPTERS[i + 1]));
    const ct = (i + leg) / (CHAPTERS.length - 1);
    const pos = this.camPos.getPoint(ct), tgt = this.camLook.getPoint(ct);
    pos.x += this.look.x * 0.18 + Math.sin(t * 0.4) * 0.02;
    pos.y += -this.look.y * 0.1 + Math.sin(t * 0.33) * 0.015;
    // The road shot follows the lead adventurer.
    const follow = Math.max(0, 1 - Math.abs(p - C(3)) / (STEP * 0.75));
    const lead = this.walkers[0];
    if (follow > 0 && lead?.obj.visible) {
      const w = lead.obj.position;
      const back = new THREE.Vector3(0, 0, -1).applyQuaternion(lead.obj.quaternion).multiplyScalar(0.42);
      pos.lerp(w.clone().add(back).add(new THREE.Vector3(0.12, 0.26, 0)), ease(follow));
      tgt.lerp(w.clone().add(new THREE.Vector3(0, 0.04, 0)), ease(follow));
    }
    // Narrow screens: pull the camera in so the table fills the frame.
    if (this.camera.aspect < 1) pos.sub(tgt).multiplyScalar(0.78).add(tgt);
    this.camera.position.copy(pos);
    this.camera.lookAt(tgt);
    this.final.uniforms.uTime.value = t;
    this.updateLife(dt, t, p);
    this.updateSet(dt, t, p);
    this.composer.render();
  }
}
